import type { MantineTheme } from '@mantine/core';
import { Anchor, Group, Stack, Tabs } from '@mantine/core';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import TabsList from '@repo/common/components/TabsList';
import { UserRole } from '@repo/common/enums/UserRole';
import type { IconNames } from '@repo/foundations';
import { Banner, Button, Icon, Text } from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import type { ITunnel } from '../../api';
import {
	queryClient,
	tunnelsQueryKeyFactory,
	useCreateTunnel,
	useTunnelList,
	useUpdateTunnel,
} from '../../api';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	closeAllModals,
	openConfirmModal,
	openModal,
} from '../../components/ModalManager';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import TableV2 from '../../components/TableV2/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import {
	CreateForwardTunnelModal,
	CreateReverseTunnelModal,
	EditCidrWhitelistModal,
} from './components';

type TunnelListPageTab = 'forward' | 'reverse';

// Type for the classes object returned by useStyles
interface TunnelPageClasses {
	wrapper: string;
	tableWrapper: string;
	tabsList: string;
	emptySpace: string;
	borderBottom: string;
}

const TUNNEL_STATUS_MAPPING: Record<
	'connected' | 'disconnected',
	{
		label: string;
		iconName: IconNames;
		iconFillColor?: ColorNames;
		iconColor?: ColorNames;
	}
> = {
	connected: {
		label: 'Connected',
		iconName: 'circleCheckFilled',
		iconFillColor: 'icon/success/default',
	},
	disconnected: {
		label: 'Disconnected',
		iconName: 'circleXFilled',
		iconFillColor: 'icon/primary/disabled',
	},
};

const getTunnelStatus = (record: ITunnel) => {
	if (record.status) {
		return 'connected';
	}
	return 'disconnected';
};

const forwardColumns: ExtendedDataTableColumn<ITunnel>[] = [
	{ accessor: 'host', titleLabel: 'Host', sortable: false },
	{ accessor: 'port', titleLabel: 'Port', sortable: false },
	{ accessor: 'username', titleLabel: 'Username', sortable: false },
	{
		accessor: 'status',
		titleLabel: 'Status',
		sortable: false,
		render: (record: ITunnel) => (
			<Group spacing="3xs" noWrap>
				<Icon
					size="md"
					name={TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconName}
					fillColor={
						TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconFillColor
					}
					color={TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconColor}
				/>
				<Text size="sm">
					{TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].label}
				</Text>
			</Group>
		),
	},
	{
		accessor: 'created_at',
		titleLabel: 'Created at',
		sortable: false,

		render: (record: { created_at: string }) =>
			moment(record.created_at).format('YYYY-MM-DD'),
	},
];

const reverseColumns = [
	{ accessor: 'port', titleLabel: 'Port' },
	{
		accessor: 'whitelist_ip',
		titleLabel: 'CIDR Whitelist',
		sortable: false,
		render: (record: { whitelist_ip?: string }) =>
			record.whitelist_ip || 'No restrictions',
	},
	{
		accessor: 'status',
		titleLabel: 'Status',
		sortable: false,
		render: (record: ITunnel) => (
			<Group spacing="3xs" noWrap>
				<Icon
					size="md"
					name={TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconName}
					fillColor={
						TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconFillColor
					}
					color={TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].iconColor}
				/>
				<Text size="sm">
					{TUNNEL_STATUS_MAPPING[getTunnelStatus(record)].label}
				</Text>
			</Group>
		),
	},
	{
		accessor: 'created_at',
		sortable: false,
		titleLabel: 'Created at',
		render: (record: { created_at: string }) =>
			moment(record.created_at).format('YYYY-MM-DD'),
	},
];

function TunnelsListPage() {
	const [activeTab, setActiveTab] = useState<TunnelListPageTab>('forward');
	const {
		classes,
		theme,
	}: { classes: TunnelPageClasses; theme: MantineTheme } = useStyles();
	const { mutateAsync: createTunnel } = useCreateTunnel({});
	const { mutateAsync: updateTunnel } = useUpdateTunnel({});
	const { data: memberEnv } = useSuspenseApiMemberEnv({});
	const { tunnelAccess } = useSuspenseFeatureAccess();

	const handleDelete = useCallback(
		async (tunnels: ITunnel[], onClose?: () => void) => {
			openConfirmModal({
				variant: 'default',
				title: 'Delete tunnel',
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				labels: {
					cancel: 'Cancel',
					confirm: 'Delete',
				},
				onConfirm: async () => {
					// We do not delete, but set `archived` to `true`.
					// This is because we need to make sure the reverse tunnel ports are not reused.
					await Promise.all(
						tunnels.map(async (tunnel) => {
							await updateTunnel({
								data: {
									id: tunnel.id,
									archived: true,
								},
							});
						})
					);

					closeAllModals();
					queryClient.invalidateQueries({
						queryKey: tunnelsQueryKeyFactory.allLists(),
					});
					onClose?.();
				},
				onClose: () => {
					closeAllModals();
				},
				children: (
					<Text>
						This will permanently remove this tunnel, and remove it from any
						integrations. This action cannot be undone.
					</Text>
				),
			});
		},
		[updateTunnel]
	);

	const handleNewForwardTunnel = useCallback(async () => {
		openModal({
			variant: 'default',
			title: 'Create forward tunnel',
			children: (
				<CreateForwardTunnelModal onCreateTunnel={createTunnel} theme={theme} />
			),
		});
	}, [createTunnel, theme]);

	const handleNewReverseTunnel = useCallback(async () => {
		openModal({
			variant: 'default',
			title: 'Create reverse tunnel',
			children: (
				<CreateReverseTunnelModal
					onCreateTunnel={createTunnel}
					tunnelUrl={memberEnv?.TUNNEL_URL ?? ''}
					theme={theme}
				/>
			),
		});
	}, [createTunnel, memberEnv?.TUNNEL_URL, theme]);

	const actions = useMemo(
		() => [
			{
				id: 'actions::edit',
				name: 'Edit',
				title: 'Edit',
				type: 'edit',
				iconName: 'edit',
				show: (tunnels: ITunnel[]) => tunnels.some((tunnel) => tunnel.reverse),
				onClick: async (tunnels: ITunnel[]) => {
					const tunnel = tunnels?.[0]; // We only support single selection for edit

					openModal({
						variant: 'default',
						title: 'Edit CIDR Whitelist',
						children: (
							<EditCidrWhitelistModal
								tunnel={tunnel}
								onUpdateTunnel={updateTunnel}
								theme={theme}
							/>
						),
					});
				},
			},
			{
				id: 'actions::delete',
				name: 'Delete',
				title: 'Delete',
				type: 'delete',
				iconName: 'trash',
				show: true,
				onClick: handleDelete,
			},
		],
		[handleDelete, updateTunnel, theme]
	);

	const handleTabChange = (value: string): void => {
		if (value === 'forward' || value === 'reverse') {
			setActiveTab(value as TunnelListPageTab);
		}
	};

	return (
		<FeatureGuard
			featureName="Tunnels"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Integrations.Create"
			isFeatureIncludedInPlan={tunnelAccess ?? false}
			isAlwaysIncludedFeature={false}
			description="Connect integrations to Secoda using SSH tunnels instead of a direction connection"
		>
			<Stack
				className={classes.wrapper}
				spacing={0}
				mt={`-${theme.other.space[2]}px`}
			>
				<Tabs
					color="fill/brand/default"
					value={activeTab}
					onTabChange={handleTabChange}
				>
					<TabsList
						tabs={[
							{ value: 'forward', label: 'Forward Tunnels' },
							...(memberEnv?.REVERSE_TUNNEL_ENABLED
								? [{ value: 'reverse', label: 'Reverse Tunnels' }]
								: []),
						]}
					/>
					<Tabs.Panel value="forward" pt={'sm'}>
						<Stack>
							<Banner
								title="Forward tunnels"
								tone="info"
								message={
									<>
										Forward tunnels connect to a SSH server (bastion host) that
										you control. Forward tunnels allow secure communication with
										your data sources over an encrypted tunnel&nbsp;
										<Anchor
											href="https://docs.secoda.com/integrations/connecting-via-tunnels/connecting-via-ssh-tunnel"
											target="_blank"
										>
											Learn more
										</Anchor>
									</>
								}
							/>
							<TableV2
								pluralTypeString="forward tunnels"
								columns={forwardColumns}
								usePaginationList={useTunnelList}
								defaultRequiredSearchParams={{ reverse: false }}
								usePaginationListOptions={{
									filters: { reverse: false, archived: false },
								}}
								withActions={actions as ICommandListItem<ITunnel>[]}
								withDialog
								withCheckbox
								withAdditionalButtons={
									<Button variant="primary" onClick={handleNewForwardTunnel}>
										Add forward tunnel
									</Button>
								}
							/>
						</Stack>
					</Tabs.Panel>
					{memberEnv?.REVERSE_TUNNEL_ENABLED && (
						<Tabs.Panel value="reverse" pt={'sm'}>
							<Stack>
								<Banner
									title="Reverse tunnels"
									tone="info"
									message={
										<>
											Reverse tunnels are SSH tunnels initiated from a docker
											container inside your network. Reverse tunnels do not
											require port forwarding, adding an additional layer of
											security&nbsp;
											<Anchor
												href="https://docs.secoda.com/integrations/connecting-via-tunnels/connecting-via-secoda-agent-reverse-ssh-tunnel"
												target="_blank"
											>
												Learn more
											</Anchor>
										</>
									}
								/>
								<TableV2
									pluralTypeString="reverse tunnels"
									columns={reverseColumns as ExtendedDataTableColumn<ITunnel>[]}
									usePaginationList={useTunnelList}
									defaultRequiredSearchParams={{ reverse: true }}
									usePaginationListOptions={{
										filters: { reverse: true, archived: false },
									}}
									withQuickActions={['actions::edit']}
									withActions={actions as ICommandListItem<ITunnel>[]}
									withDialog
									withCheckbox
									withAdditionalButtons={
										<Button variant="primary" onClick={handleNewReverseTunnel}>
											Add reverse tunnel
										</Button>
									}
								/>
							</Stack>
						</Tabs.Panel>
					)}
				</Tabs>
			</Stack>
		</FeatureGuard>
	);
}

export default TunnelsListPage;
